import { GridApi, IServerSideGetRowsParams } from '@ag-grid-community/core';
import { Injector } from '@angular/core';
import moment from 'moment';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';

import {
	Document,
	FieldOfDocument
} from '../../../../commercial-documents/models/document';
import { DocumentService } from '../../../../commercial-documents/services/documents.service';
import { FilterModel } from '../../../models/ag-grid';
import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';
import { DATE_FORMAT_PATTERN_DD_MM_YYYY, FilterDateFormatType,
	WEEKYEAR_FORMAT_PATTERN } from '../../../models/filter-formats';
import { PaginatedResult } from '../../../models/paginated-result';
import { DatacenterSelectionService } from '../../../services/datacenter-selection.service';
import { OrderBy, OrderByDirection } from '../../../utils/constant';
import { ColDefWithFields, ColumnType } from '../../column-type';
import { CommonGridOptions } from '../../grid-options';
import { Strategy } from '../strategy';

type RecursivePartial<T> = {
	[K in keyof T]?: T[K] extends (infer R)[] ? RecursivePartial<R>[] : RecursivePartial<T[K]>
};

export class StrategyDocument implements Strategy<Document> {
	documentService: DocumentService;
	selectionService: DatacenterSelectionService;
	defaultOrderBy = new OrderBy<keyof Document>('date', OrderByDirection.DESC);
	hasTotal = false;
	aggregateFields = [];

	constructor(injector: Injector) {
		this.documentService = injector.get<DocumentService>(DocumentService);
		this.selectionService = injector.get<DatacenterSelectionService>(DatacenterSelectionService);
	}

	setTotal(_api?: GridApi, _filter?: string): Subscription {
		throw new Error('Method not implemented.');
	}

	async getAllDataSelected(
		currentColumns: string[],
		filter: string | undefined,
		orderBy: OrderBy<keyof Document>[],
	) {
		return this.getAllData(orderBy, currentColumns, filter)
			.then((documents: PaginatedResult<Document>) => this.removeDeselectedRows(documents));
	}

	async getAllData(
		orderBy: OrderBy<keyof Document>[],
		currentColumns: string[],
		filter: string | undefined
	) {
		const limit = 5000;
		const maxCalls = 12;
		const maxRows = limit * maxCalls;

		const firstPage = await firstValueFrom(this.documentService.getDocuments(
			<FieldOfDocument[]> currentColumns,
			filter,
			0,
			limit,
			orderBy,
			false
		));

		const totalCount = firstPage.count;
		const targetRows = Math.min(totalCount, maxRows);
		const totalCalls = Math.ceil(targetRows / limit);
		const results: Document[] = [...firstPage.results];

		for (let callIndex = 1; callIndex < totalCalls; callIndex++) {
			const page = await firstValueFrom(this.documentService.getDocuments(
				<FieldOfDocument[]> currentColumns,
				filter,
				callIndex * limit,
				limit,
				orderBy,
				false
			));
			if (page.results.length === 0 && callIndex * limit < targetRows) {
				throw new Error('Document pagination returned an empty page before all results were fetched');
			}
			results.push(...page.results);
		}

		if (results.length > targetRows) {
			results.length = targetRows;
		}

		return { results, count: totalCount, page: 1, limit: results.length };
	}

	formatCellByType(columnDef: ColDefWithFields<Document>, data: Document) {
		const field = columnDef.field || '';
		const fields = columnDef.fields || [];
		const { columnType, colId } = columnDef;
		switch (columnType) {
		case ColumnType.ORDER_NUMBER_COLUMN:
			return this.formatOrderNumberColumn(fields, colId, data) ?? '';
		case ColumnType.NUMBER_COLUMN:
			return this.formatNumberColumn(columnDef, data) ?? '';
		case ColumnType.TEXT_COLUMN:
			return this.formatTextColumn(field, colId, data) ?? '';
		case ColumnType.DATE_COLUMN:
			return this.formatDateColumn(columnDef, data) ?? '';
		default:
			throw new Error('unknown filter type');
		}
	}

	getCustomGridOptions(defaultGridOptions: CommonGridOptions) {
		return new CommonGridOptions<Document>({
			...defaultGridOptions,
			getRowId: ({ data }: { data: Document }) => String(data.id),
		});
	}

	getDataFromGrid(
		orderBy: OrderBy<keyof Document>[],
		currentColumns: FieldOfDocument[],
		filter: string,
		params: IServerSideGetRowsParams<Document>,
		rowsCountSubject: BehaviorSubject<number | undefined>
	) {
		const currentColumnsWithId = this.addIdColumn(currentColumns);
		return this.documentService.getDocuments(
			currentColumnsWithId,
			filter,
			params.request.startRow ?? 0,
			250,
			orderBy,
			false
		).subscribe({
			next: documents => {
				const lastRow = params.request.endRow === undefined || documents.count <= params.request.endRow
					? documents.count
					: -1;
				params.success({ rowData: documents.results, rowCount: lastRow });
				rowsCountSubject.next(documents.count);
			},
			error: (err: unknown) => {
				console.error('error requesting document', err);
				rowsCountSubject.next(undefined);
				params.fail();
			}
		});
	}

	addIdColumn(currentColumns: FieldOfDocument[]) {
		if (!currentColumns.includes('id')) {
			currentColumns.push('id');
		}
		return currentColumns;
	}

	createAggregateCustomViewFilter(_view: UserDatacenterView): string {
		throw new Error('Method not implemented.');
	}

	createAggregateQuickSearchFilter(
		_currentSelectedView: UserDatacenterView,
		_api?: GridApi,
		_filterModel?: FilterModel
	): string {
		throw new Error('Method not implemented.');
	}

	private removeDeselectedRows(documents: PaginatedResult<Document>) {
		return documents.results.filter(document => {
			const rowDeselected = this.selectionService.findDeselectedNode(
				String(document.id)
			);
			return !rowDeselected;
		});
	}

	private formatTextColumn(field: string, colId: string | undefined, document?: RecursivePartial<Document>) {
		if (!colId || document === undefined) {
			return '';
		}

		switch (colId) {
		case 'batchNumber':
			return document.documentLines?.map(item => item.batchNumber)
				.filter((value, index, self) => self.indexOf(value) === index).join(';');
		case 'steelgrade':
			return document.documentLines?.map(item => item.steelGrade)
				.filter((value, index, self) => self.indexOf(value) === index).join(';');
		case 'dispatchAdviceNumber':
			if (document.type && (document.type === 'DA')) {
				return document.name;
			}
			return document.documentLines?.map(item => item.dispatchAdviceNumber)
				.filter((value, index, self) => self.indexOf(value) === index).join(';');
		case 'transportReference':
			return document.documentLines?.map(item => item.transportReference)
				.filter((value, index, self) => self.indexOf(value) === index).join(';');
		case 'customerArticleReference':
			return document.documentLines?.map(item => item.customerArticleReference)
				.filter((value, index, self) => self.indexOf(value) === index).join(';');
		case 'invoiceCurrency':
			return document.invoiceCurrency;
		case 'invoicedQuantityUnit': {
			const unitsFromLines = document.documentLines?.map(item => item.invoicedQuatityUnit)
				.filter((value): value is string => value !== undefined && value !== '')
				.filter((value, index, self) => self.indexOf(value) === index)
				.join(';');
			if (unitsFromLines) {
				return unitsFromLines;
			}
			return this.extractInvoicedQuantityEntries(document.invoicedQuantity).map(entry => entry.unit).join(';');
		}
		case 'heatNumber':
			return document.documentLines?.map(item => item.heatNumber)
				.filter((value, index, self) => self.indexOf(value) === index).join(';');
		case 'mill':
			if (document.type === 'CE' || document.type === 'DA') {
				return document.millDescription;
			}
			return document.documentLines?.map(item => item.millDescription)
				.filter((value, index, self) => self.indexOf(value) === index).join(';');
		case 'customer':
			if (document.type && (document.type === 'IN' || document.type === 'XCARB')) {
				return document.documentLines?.filter(item => item.soldtoName && item.soldtoBusinessId)
					.map(item => `${ item.soldtoName }(${ item.soldtoBusinessId })`)
					.filter((value, index, self) => self.indexOf(value) === index).join(';');
			}
			return `${ document.soldtoName }(${ document.soldtoBusinessId })`;
		case 'endUser':
			return document.documentLines?.filter(item => item.enduserName && item.enduserBusinessId)
				.map(item => `${ item.enduserName }(${ item.enduserBusinessId })`)
				.filter((value, index, self) => self.indexOf(value) === index).join(';');
		case 'invoiceNumber':
			if (document.type && (document.type === 'IN')) {
				return document.name;
			}
			return document.invoiceNumber;
		case 'shipto':
			if (document.type && (document.type === 'IN' || document.type === 'XCARB')) {
				return document.documentLines?.filter(item => item.shiptoName && item.shiptoBusinessId)
					.map(item => `${ item.shiptoName }(${ item.shiptoBusinessId })`)
					.filter((value, index, self) => self.indexOf(value) === index).join(';');
			}
			return `${ document.shiptoName }(${ document.shiptoBusinessId })`;
		case 'type':
			return document.typeDescription;
		case 'subtype':
			return document.subtypeDescription;
		case 'transportMode':
			return document.transportModeDescription;
		case 'payer':
			if (document.type && document.type === 'XCARB') {
				return document.documentLines?.filter(item => item.payerName && item.payerBusinessId)
					.map(item => `${ item.payerName }(${ item.payerBusinessId })`)
					.filter((value, index, self) => self.indexOf(value) === index).join(';');
			}
			return document.payerName && document.payerBusinessId
				? `${ document.payerName }(${ document.payerBusinessId })` : '';
		case 'endQuarter':
			return document.documentLines?.map(item => item.endQuarter)
				.filter((value, index, self) => self.indexOf(value) === index).join(';');
		// No default
		}
		return <string> document[<keyof Document> field];
	}

	private formatOrderNumberColumn(
		fields: string[],
		colId: string | undefined,
		document?: RecursivePartial<Document>
	) {
		if (!colId || document === undefined) {
			return '';
		}

		switch (colId) {
		case 'customerOrderNumber':
			if (document.type === 'CE' || document.type === 'OA') {
				return document.documentLines?.map(item => {
					if (document.customerOrderReference !== undefined
						&& item.customerOrderItemReference !== undefined) {
						return `${ document.customerOrderReference }/${ item.customerOrderItemReference }`;
					} else if (document.customerOrderReference !== undefined
						&& item.customerOrderItemReference === undefined) {
						return document.customerOrderReference;
					}
					return '';
				}).filter((value, index, self) => self.indexOf(value) === index).join(';');
			}
			return document.documentLines?.map(item => {
				if (item.customerOrderReference !== undefined
					&& item.customerOrderItemReference !== undefined) {
					return `${ item.customerOrderReference }/${ item.customerOrderItemReference }`;
				} else if (item.customerOrderReference !== undefined
					&& item.customerOrderItemReference === undefined) {
					return item.customerOrderReference;
				}
				return '';
			}).filter((value, index, self) => self.indexOf(value) === index).join(';');
		case 'supplierOrderNumber':
			if (document.type === 'CE' || document.type === 'OA') {
				return document.documentLines?.map(item => {
					if (document.supplierOrderReference !== undefined
						&& item.supplierOrderReferenceItem !== undefined) {
						return `${ document.supplierOrderReference }/${ item.supplierOrderReferenceItem }`;
					} else if (document.supplierOrderReference !== undefined
						&& item.supplierOrderReferenceItem === undefined) {
						return document.supplierOrderReference;
					}
					return '';
				}).filter((value, index, self) => self.indexOf(value) === index).join(';');
			}
			return document.documentLines?.map(item => {
				if (item.supplierOrderReference !== undefined
					&& item.supplierOrderReferenceItem !== undefined) {
					return `${ item.supplierOrderReference }/${ item.supplierOrderReferenceItem }`;
				} else if (item.supplierOrderReference !== undefined
					&& item.supplierOrderReferenceItem === undefined) {
					return item.supplierOrderReference;
				}
				return '';
			}).filter((value, index, self) => self.indexOf(value) === index).join(';');
		case 'millOrderNumber':
			return document.documentLines?.map(item => {
				if (item.millOrderReference !== undefined
					&& item.millOrderReferenceItem !== undefined) {
					return `${ item.millOrderReference }/${ item.millOrderReferenceItem }`;
				} else if (item.millOrderReference !== undefined
					&& item.millOrderReferenceItem === undefined) {
					return item.millOrderReference;
				}
				return '';
			}).filter((value, index, self) => self.indexOf(value) === index).join(';');
		// No default
		}
		let orderNumber = '';
		if (<string>document[<keyof Document>fields[0]]) {
			orderNumber = <string>document[<keyof Document>fields[0]];
		}
		if (<string>document[<keyof Document>fields[0]] && <string>document[<keyof Document>fields[1]]) {
			orderNumber += `/${ <string>document[<keyof Document>fields[1]] }`;
		}
		return orderNumber;
	}

	private formatDateColumn(columnDef: ColDefWithFields<Document>, document?: RecursivePartial<Document>) {
		const dateformat = (<FilterDateFormatType> columnDef.cellRendererParams).outputFormat === 'week'
			? WEEKYEAR_FORMAT_PATTERN : DATE_FORMAT_PATTERN_DD_MM_YYYY;
		if (columnDef.colId === undefined || !document) {
			return '';
		}

		if (columnDef.colId === 'date' && document.date) {
			return moment.utc(document.date).local().format(dateformat);
		}

		if (columnDef.colId === 'paymentDueDate' && document.paymentDueDate) {
			return moment.utc(document.paymentDueDate).local().format(dateformat);
		}

		if (columnDef.colId === 'dispatchAdviceDate') {
			if (document.type === 'CE' || document.type === 'IN') {
				return document.documentLines?.map(item => moment.utc(item.dispatchAdviceDate).local()
					.format(dateformat)).filter((value, index, self) => self.indexOf(value) === index).join(';');
			} else if (document.type === 'DA') {
				return moment.utc(document.dispatchAdviceDate).local().format(dateformat);
			}
			return '';
		} else if (document[<keyof Document> columnDef.field]) {
			return moment.utc(<string> document[<keyof Document> columnDef.field]).format(dateformat);
		}
		return '';
	}

	private formatNumberColumn(columnDef: ColDefWithFields<Document>, document?: RecursivePartial<Document>) {
		if (!columnDef.colId || document === undefined) {
			return '';
		}

		switch (columnDef.colId) {
		case 'invoicedQuantityValue': {
			const serializedInvoicedQuantity = document.invoicedQuantity ?? '';
			const entries = this.extractInvoicedQuantityEntries(serializedInvoicedQuantity);
			if (entries.length > 0) {
				return entries.map(entry => entry.quantity).join(';');
			}
			return serializedInvoicedQuantity;
		}
		case 'invoiceAmount':
			return this.formatInvoiceNumberForExport(document.invoiceAmount);
		case 'invoiceAmountWithoutVat':
			return this.formatInvoiceNumberForExport(document.invoiceAmountWithoutVat);
		case 'invoiceTaxAmount':
			return this.formatInvoiceNumberForExport(document.invoiceTaxAmount);
		case 'orderedQuantity':
			return document.documentLines?.filter(item => item.orderedQuantity !== undefined)
				.map(item =>
					item.orderedQuantity === undefined ? ''
						: String(item.orderedQuantity))
				.join(';');
		// No default
		}
		const value = <number | undefined >document[<keyof Document> columnDef.field];
		return value === undefined ? '' : String(value);
	}

	private formatInvoiceNumberForExport(value?: number | null) {
		if (value === undefined || value === null) {
			return '';
		}
		return value === 0 ? '0.00' : String(value);
	}

	private extractInvoicedQuantityEntries(invoicedQuantity?: string) {
		if (!invoicedQuantity) {
			return [];
		}

		return invoicedQuantity
			.split(';')
			.map(value => value.trim())
			.filter(value => value.length > 0)
			.map(value => {
				const firstSpaceIndex = value.indexOf(' ');
				if (firstSpaceIndex <= 0 || firstSpaceIndex >= value.length - 1) {
					return null;
				}

				const quantity = value.slice(0, firstSpaceIndex).trim();
				const unit = value.slice(firstSpaceIndex + 1).trim();
				if (!quantity || !unit) {
					return null;
				}

				return { quantity, unit };
			})
			.filter((value): value is { quantity: string; unit: string } => value !== null);
	}
}
